import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CalendarClock, Pencil, Send, Share2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import type { DocumentStatus } from '@fint/validation';
import { formatRateHuman } from '@fint/money';
import {
  Badge, Button, Card, CardContent, ErrorState, Money, SkeletonTable, StatusBadge,
} from '@/features/common/ui';
import { Attachments } from '@/features/common/Attachments';
import { DocumentSection, documentQueryKey } from '@/features/documents/DocumentSection';
import { isImportedDocument, originallyTitle, type ImportedDocument } from './imported';
import { InvoicePayments } from './InvoicePayments';
import { ShareInvoiceSheet } from './ShareInvoiceSheet';
import { RepeatInvoiceSheet } from '@/features/recurring/RepeatInvoiceSheet';
import { InvoiceHistory } from './InvoiceHistory';
import { PaymentExpectation } from './PaymentExpectation';

interface InvoiceDetail extends ImportedDocument {
  id: string;
  number: string;
  /** 1 while `number` is a DRAFT- placeholder; issuing takes the invoice number (0025). */
  provisionalNumber?: number | null;
  status: DocumentStatus;
  /** The original this invoice was recreated from, when it was imported (0018). */
  sourceFile?: { id: string; filename: string } | null;
  date: string;
  dueDate: string | null;
  currency: string;
  total: string;
  balanceDue: string;
  /** The book's currency and the rate the document was booked at (0021). */
  baseCurrency?: string | null;
  baseTotal?: string | null;
  exchangeRate?: string | null;
  exchangeRateSource?: string | null;
  writtenOffAmount?: string | null;
  writtenOffReason?: string | null;
  gstSplitKind: string | null;
  /** When the money is really expected (0039): forecast and reminders only. */
  expectedPaymentOn?: string | null;
  paymentNotExpected?: number | null;
  paymentExpectationNote?: string | null;
  remindersPaused?: number | null;
  customer: { id: string; displayName: string; email: string | null; gstin: string | null; gstTreatment?: string | null } | null;
}

/**
 * One invoice, as it stands.
 *
 * ── THE RECORD ABOVE, THE DOCUMENT BELOW ──
 * The top of the page is the invoice as a RECORD: its status, what it is
 * owed, the actions that change it, the files attached to it. Below that is
 * the invoice as a DOCUMENT - the letterhead, the lines, the words - fetched
 * from the server's own document view and rendered by the same component
 * the proforma uses, and it is the only part of this page that prints. It
 * used to be that this page's own table was what got printed, with the
 * navigation hidden; the customer's copy now comes from one renderer, on
 * screen, on paper and as the PDF.
 *
 * Every figure here is read back from the SERVER - none is recomputed in the
 * browser. The composer's live preview is a preview; this is the record.
 */
export function InvoiceDetailPage() {
  // "Repeat this" - the whole creation path for a subscription, because the
  // lines and the customer come from a document already priced.
  const [repeating, setRepeating] = useState(false);
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const org = useActiveOrg();
  const perms = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sharing, setSharing] = useState(false);

  const query = useQuery({
    // Singular resource for a detail, orgId always second.
    queryKey: ['invoice', org?.id, invoiceId],
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${invoiceId}`),
    enabled: !!org?.id && !!invoiceId,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['invoice', org?.id, invoiceId] });
    // Issuing or voiding changes what the document says of itself.
    if (invoiceId) void queryClient.invalidateQueries({ queryKey: documentQueryKey('invoices', org?.id, invoiceId) });
    // The list and the dashboard both show this invoice's status and balance.
    void queryClient.invalidateQueries({ queryKey: ['invoices', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
  };

  const send = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/send`),
    onSuccess: refresh,
  });
  const voidInvoice = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/void`, {}),
    onSuccess: refresh,
  });

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Invoice" />
        <div className="px-5">
          <Card>
            <SkeletonTable rows={5} cols={4} />
          </Card>
        </div>
      </>
    );
  }

  if (query.isError || !query.data) {
    return (
      <>
        <PageHeader title="Invoice" />
        <div className="px-5">
          <Card>
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          </Card>
        </div>
      </>
    );
  }

  const invoice = query.data;
  const actionError = (send.error ?? voidInvoice.error) instanceof ApiError
    ? ((send.error ?? voidInvoice.error) as ApiError)
    : undefined;
  const imported = isImportedDocument(invoice);
  const originally = originallyTitle(invoice.externalNumber, invoice.sourceSystem, invoice.number);

  return (
    <>
      {/* The page's own chrome is not part of the document: nothing above
          the document section prints. */}
      <div className="print:hidden">
        <PageHeader
          title={invoice.number}
          /* A draft number on screen has to say it is one, or the person
             reading it files it away as the invoice number and then finds a
             different one on the PDF the customer gets. */
          subtitle={
            invoice.provisionalNumber === 1
              ? `${invoice.customer?.displayName ?? 'Draft'} · a draft number - issuing this takes the next invoice number`
              : invoice.customer?.displayName
          }
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}>
                <ArrowLeft className="size-4" aria-hidden />
                All invoices
              </Button>
              {invoice.status !== 'void' && perms.can('invoice', 'create') ? (
                <Button variant="outline" size="sm" onClick={() => setRepeating(true)} data-testid="invoice-repeat">
                  <CalendarClock className="size-4" aria-hidden />
                  Repeat
                </Button>
              ) : null}
              {invoice.status === 'draft' && perms.can('invoice', 'edit') ? (
                <>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/invoices/${invoice.id}/edit`} data-testid="invoice-edit">
                      <Pencil className="size-4" aria-hidden />
                      Edit
                    </Link>
                  </Button>
                  <Button size="sm" loading={send.isPending} onClick={() => send.mutate()}>
                    <Send className="size-4" aria-hidden />
                    Issue
                  </Button>
                </>
              ) : null}
              {/* An ISSUED invoice can be edited too - by someone who may
                  restate a figure the customer holds - and the composer
                  asks why, and puts the before and after on the record. */}
              {invoice.status !== 'void' && invoice.status !== 'draft' && invoice.status !== 'written_off' && perms.can('invoice', 'manage') ? (
                <Button asChild variant="outline" size="sm">
                  <Link to={`/invoices/${invoice.id}/edit`} data-testid="invoice-edit-issued">
                    <Pencil className="size-4" aria-hidden />
                    Edit
                  </Link>
                </Button>
              ) : null}
              {invoice.status !== 'void' && invoice.status !== 'draft' ? (
                /* WhatsApp, email, a link - see ShareInvoiceSheet. Anyone who may see the invoice may send it. */
                <Button size="sm" onClick={() => setSharing(true)} data-testid="invoice-share">
                  <Share2 className="size-4" aria-hidden />
                  Share
                </Button>
              ) : null}
              {invoice.status !== 'void' && invoice.status !== 'draft' && perms.can('invoice', 'manage') ? (
                <Button variant="outline" size="sm" loading={voidInvoice.isPending} onClick={() => voidInvoice.mutate()}>
                  <Ban className="size-4" aria-hidden />
                  Void
                </Button>
              ) : null}
            </>
          }
        />
      </div>

      {sharing ? <ShareInvoiceSheet invoiceId={invoice.id} number={invoice.number} onClose={() => setSharing(false)} /> : null}
      {repeating ? (
        <RepeatInvoiceSheet
          invoice={{
            id: invoice.id,
            number: invoice.number,
            date: invoice.date,
            customerName: invoice.customer?.displayName ?? null,
          }}
          onClose={() => setRepeating(false)}
          onCreated={() => {
            setRepeating(false);
            navigate('/recurring');
          }}
        />
      ) : null}

      <div className="space-y-4 px-4 pb-10 sm:px-5 print:p-0">
        {/*
          ── WHERE IT CAME FROM ──
          Under the number, on every width - the header's subtitle is hidden
          on a phone, and the number the customer's accountant quotes is not
          a detail a phone can do without. The badge says the invoice was
          recreated here; the line says from what, in the words the Inbox and
          the list use, so a person reads one form of words everywhere.
        */}
        {imported || originally ? (
          <p className="-mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground print:hidden" data-testid="invoice-origin">
            {/* The badge and the line say the same thing when the invoice kept its number - one of them is enough. */}
            {imported && !originally?.startsWith('Imported') ? <Badge variant="info">Imported</Badge> : null}
            {originally ? <span>{originally}</span> : null}
          </p>
        ) : null}

        {actionError ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive print:hidden">
            {actionError.message}
          </p>
        ) : null}

        <div className="grid gap-4 print:hidden sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Status" value={<StatusBadge status={invoice.status} />} />
          <Fact label="Invoice date" value={formatDate(invoice.date)} />
          <Fact label="Due date" value={formatDate(invoice.dueDate)} />
          <Fact
            label="Balance due"
            testId="invoice-balance-due"
            value={<Money value={invoice.balanceDue} currency={invoice.currency} className="text-base font-semibold" />}
          />
        </div>
        {/* A document in another currency: what it is in the book's, and at
            what rate - the figure every report and the return carry for it. */}
        {invoice.baseCurrency && invoice.currency !== invoice.baseCurrency && invoice.exchangeRate ? (
          <p className="text-sm text-muted-foreground print:hidden" data-testid="invoice-booked-at">
            Booked at 1 {invoice.currency} = {formatRateHuman(invoice.exchangeRate)} {invoice.baseCurrency}
            {invoice.exchangeRateSource === 'fbil' ? ' (FBIL reference rate)' : invoice.exchangeRateSource === 'ecb' ? ' (ECB reference rate)' : ''}
            {' = '}
            <Money value={invoice.baseTotal ?? '0'} currency={invoice.baseCurrency} /> in the books.
          </p>
        ) : null}

        {/* Paying late is not a void: say when, and the forecast and reminders follow. */}
        <PaymentExpectation invoice={invoice} canEdit={perms.can('invoice', 'edit')} />

        <DocumentSection path="invoices" id={invoice.id} />

        {/* What has come in against it, instalment by instalment, each with
            its receipt - and "Record a payment" that knows this invoice. */}
        <InvoicePayments invoice={invoice} />

        {/* Issued, edited (why, and what changed), voided - the invoice's own trail. */}
        <InvoiceHistory invoiceId={invoice.id} />

        {/* The source - the old system's PDF - is listed first, marked as
            such; see `sourceFirst` in Attachments.tsx. */}
        <div className="print:hidden">
          <Attachments
            ownerType="invoice"
            ownerId={invoice.id}
            uploadAs={imported && !invoice.sourceFile ? 'source' : undefined}
            emptyHint={
              imported
                ? 'The original this invoice was made from goes here as its source; signed copies and delivery proof beside it.'
                : 'Signed copies, delivery proof, a customer purchase order. Drag one here or paste a screenshot.'
            }
          />
        </div>

        {invoice.customer ? (
          <p className="text-xs text-muted-foreground print:hidden">
            Billed to{' '}
            <Link to="/customers" className="text-primary hover:underline">
              {invoice.customer.displayName}
            </Link>
            {invoice.customer.gstin ? ` · GSTIN ${invoice.customer.gstin}` : ''}
            {invoice.gstSplitKind ? ` · ${invoice.gstSplitKind.replace('_', '-')} supply` : ''}
          </p>
        ) : null}
      </div>
    </>
  );
}

function Fact({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <div className="mt-1.5 text-sm">{value}</div>
      </CardContent>
    </Card>
  );
}
